/**
 * Digest Pulse Report Templates
 * Based on best practices from Morning Brew, theSkimm, Lenny's Newsletter
 */

import { PulseReportTemplate } from "./types";

export const WEEKLY_DIGEST: PulseReportTemplate = {
  id: "weekly-digest",
  name: "Weekly Digest",
  reportType: "digest",
  description: "Comprehensive weekly summary of all activities",
  purpose:
    "Keep users engaged with a complete overview of their weekly performance, usage, and highlights.",
  icon: "CalendarOutlined",
  color: "#6366f1",
  suggestedHtmlTemplateId: "weekly-digest",
  suggestedSubject: "Your Week in Review - {{week_start}} to {{week_end}}",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "All active users",
  expectedOutcomes: [
    "Increased platform engagement",
    "Better user awareness of features",
    "Higher retention through habit formation",
    "Reduced support tickets",
  ],
  bestPractices: [
    "Send on Monday mornings for best open rates",
    "Keep sections scannable with clear headers",
    "Highlight the most impactful metrics first",
    "Include actionable next steps",
  ],
  keyMetrics: [
    "Total conversations",
    "Response rate",
    "Top performing bots",
    "Usage trends",
    "Weekly highlights",
  ],
  tags: ["digest", "weekly", "summary", "overview", "engagement"],
  bestFor: ["User engagement", "Retention", "Platform awareness"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Weekly activity summary with key highlights",
};

export const MONTHLY_DIGEST: PulseReportTemplate = {
  id: "monthly-digest",
  name: "Monthly Digest",
  reportType: "digest",
  description: "Complete monthly roundup with trends and insights",
  purpose:
    "Provide users with a monthly retrospective showing growth, trends, and achievements.",
  icon: "BarChartOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Your {{current_month}} Report - Month in Review",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "All users",
  expectedOutcomes: [
    "Clear ROI demonstration",
    "Trend visibility for planning",
    "Increased user satisfaction",
    "Budget justification data",
  ],
  bestPractices: [
    "Compare to previous month",
    "Highlight biggest wins",
    "Show progress toward goals",
    "Include recommendations for next month",
  ],
  keyMetrics: [
    "Monthly growth %",
    "Total conversations",
    "Resolution rate",
    "Customer satisfaction",
    "Cost savings",
  ],
  tags: ["digest", "monthly", "summary", "trends", "analytics"],
  bestFor: ["Executive reporting", "ROI tracking", "Strategic planning"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Monthly roundup with trends and comparisons",
};

export const QUARTERLY_DIGEST: PulseReportTemplate = {
  id: "quarterly-digest",
  name: "Quarterly Business Review",
  reportType: "digest",
  description: "Executive-level quarterly performance summary",
  purpose:
    "Deliver a comprehensive quarterly report suitable for executive review and board meetings.",
  icon: "FundOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Q{{quarter}} {{year}} Performance Report",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: true,
    team: true,
  },
  targetAudience: "Executives and decision makers",
  expectedOutcomes: [
    "Strategic decision support",
    "Budget allocation insights",
    "Stakeholder alignment",
    "Performance benchmarking",
  ],
  bestPractices: [
    "Focus on high-level trends",
    "Include YoY comparisons",
    "Provide actionable recommendations",
    "Keep it executive-summary friendly",
  ],
  keyMetrics: [
    "Quarterly growth",
    "Cost per conversation",
    "ROI metrics",
    "Customer satisfaction trends",
    "Feature adoption",
  ],
  tags: ["digest", "quarterly", "executive", "business-review", "strategy"],
  bestFor: ["Board meetings", "Executive reviews", "Strategic planning"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Executive quarterly business review",
};

export const ACTIVITY_DIGEST: PulseReportTemplate = {
  id: "activity-digest",
  name: "Daily Activity Digest",
  reportType: "digest",
  description: "Quick daily snapshot of yesterday's activity",
  purpose:
    "Provide a brief daily update for users who want to stay closely informed.",
  icon: "ThunderboltOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "weekly-digest",
  suggestedSubject: "Yesterday's Activity - {{conversations_count}} conversations",
  defaultSchedule: {
    type: "daily",
    time: "08:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "Power users and admins",
  expectedOutcomes: [
    "Real-time awareness",
    "Quick issue detection",
    "Daily habit formation",
    "Operational visibility",
  ],
  bestPractices: [
    "Keep it extremely brief",
    "Highlight only key numbers",
    "Flag any anomalies",
    "Send early morning",
  ],
  keyMetrics: [
    "Yesterday's conversations",
    "Response time",
    "Resolution rate",
    "Active bots",
  ],
  tags: ["digest", "daily", "activity", "quick", "snapshot"],
  bestFor: ["Operations teams", "Power users", "Admin monitoring"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Quick daily activity snapshot",
};

export const DIGEST_TEMPLATES = [
  WEEKLY_DIGEST,
  MONTHLY_DIGEST,
  QUARTERLY_DIGEST,
  ACTIVITY_DIGEST,
];
